import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useCan } from '../hooks/useMe';
import fmtCurrency from '../quotes/catalog/format';
import Layout from '../components/Layout';
import UsersTable from '../components/UsersTable';
import AccountTimeline from '../components/AccountTimeline';
import AccountTasks from '../components/AccountTasks';
import AccountSessions from '../components/AccountSessions';
import AccountContacts from '../components/AccountContacts';
import AccountImplementation from '../components/AccountImplementation';
import StatusChip, { RenewalChip, TierChip } from '../components/StatusChip';
import { bands, renewalStatuses, BILLING_FREQUENCIES } from '../theme';
import api from '../services/api';
import { useI18n } from '../i18n';

function colorFor(value) {
  if (value >= 70) return bands.healthy.color;
  if (value >= 40) return bands.watch.color;
  return bands.at_risk.color;
}

// Etiqueta común para que los tres campos de la rejilla alineen.
function FieldLabel({ children }) {
  return (
    <Typography sx={{
      fontSize: 10.5, fontWeight: 500, letterSpacing: '0.09em',
      textTransform: 'uppercase', color: '#847782', mb: 0.4,
    }}>{children}</Typography>
  );
}

function ClassificationSwitch({ label, hint, checked, disabled, onChange }) {
  return (
    <FormControlLabel
      sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
      control={
        <Switch
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          sx={{ mt: -0.4 }}
        />
      }
      label={
        <Box sx={{ pt: 0.3 }}>
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.3 }}>{label}</Typography>
          <Typography sx={{ fontSize: 11.5, color: '#847782', lineHeight: 1.4 }}>{hint}</Typography>
        </Box>
      }
    />
  );
}

function OwnerSelect({ label, value, options, disabled, onChange, t }) {
  return (
    <Box>
      <FieldLabel>{label}</FieldLabel>
      <TextField
        select fullWidth size="small" variant="standard"
        value={value || ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        sx={{ '& .MuiSelect-select': { fontSize: 14, fontFamily: 'Saira' } }}
      >
        <MenuItem value="" sx={{ fontSize: 14, color: '#847782' }}>
          {t('common.unassigned')}
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={o.id} value={o.id} sx={{ fontSize: 14 }}>
            {o.display_name}
            {!o.access_granted && (
              <Box component="span" sx={{ color: '#AE5B61', fontSize: 11.5, ml: 1 }}>
                · {t('accountDetail.ownerNoAccess')}
              </Box>
            )}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}

// Avance de capacitación: la barra es lo contratado, el número de abajo se
// edita en sitio. Sin objetivo pactado la barra queda vacía y el porcentaje
// en guion — no hay nada que completar.
function TrainingMeter({ account, draft, onDraft, onSave, disabled, t }) {
  const pct = account.training_pct;
  const color = pct === null ? '#847782' : colorFor(pct);

  return (
    <Box sx={{ minWidth: 176 }}>
      <FieldLabel>{t('accountDetail.training')}</FieldLabel>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{
          flexGrow: 1, minWidth: 80, height: 6, borderRadius: 3,
          backgroundColor: '#EDE6E3', overflow: 'hidden',
        }}>
          <Box sx={{ width: `${pct || 0}%`, height: '100%', backgroundColor: color }} />
        </Box>
        <Typography sx={{
          fontSize: 14, fontWeight: 600, color, fontVariantNumeric: 'tabular-nums',
        }}>
          {pct === null ? t('common.dash') : `${pct}%`}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, mt: 0.5 }}>
        <Typography sx={{ fontSize: 11.5, color: '#847782' }}>
          {t('accountDetail.trainingDone', { count: account.trainings_done })} ·
        </Typography>
        <TextField
          type="number" size="small" variant="standard" disabled={disabled}
          value={draft}
          onChange={(e) => onDraft(e.target.value)}
          onBlur={onSave}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          placeholder={t('common.dash')}
          slotProps={{ htmlInput: { min: 0, style: { width: 32, textAlign: 'center' } } }}
          sx={{ '& input': { fontSize: 11.5, fontFamily: 'Saira', py: 0 } }}
        />
        <Typography sx={{ fontSize: 11.5, color: '#847782' }}>
          {t('accountDetail.trainingTarget')}
        </Typography>
      </Box>
    </Box>
  );
}

function Fact({ label, children }) {
  return (
    <Box sx={{ minWidth: 130 }}>
      <FieldLabel>{label}</FieldLabel>
      <Box sx={{ fontSize: 14 }}>{children}</Box>
    </Box>
  );
}

export default function AccountDetail() {
  const { tenantId } = useParams();
  const [params] = useSearchParams();
  const source = params.get('source') || 'mpindustries';
  const navigate = useNavigate();
  const { t, tFlag, tApiError, fmtDate } = useI18n();

  const [account, setAccount] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [trainingDraft, setTrainingDraft] = useState('');
  const [owners, setOwners] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const canEditCs = useCan('edit_cs');
  const canEditCommercial = useCan('edit_commercial');
  const canReadQuotes = useCan('read_quotes');

  const load = useCallback(() => {
    return Promise.all([
      api.get(`/api/accounts/${tenantId}`, { params: { source } }),
      api.get(`/api/accounts/${tenantId}/users`, { params: { source } }),
      api.get('/api/owners'),
    ])
      .then(([a, u, o]) => {
        setAccount(a.data);
        setUsers(u.data);
        setOwners(o.data);
        setNameDraft(a.data.display_name || a.data.product_org_name || '');
        setTrainingDraft(a.data.training_target ?? '');
      })
      // Un 403 aquí no es un fallo de carga: la cuenta existe y es de alguien
      // más. Decirlo evita que parezca un error de la herramienta.
      .catch((err) => setErrorKey(err.response?.status === 403
        ? 'apiErrors.ACCOUNT_FORBIDDEN'
        : 'accountDetail.loadError'))
      .finally(() => setLoading(false));
  }, [tenantId, source]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canReadQuotes) return;
    api.get('/api/quotes', { params: { tenantId } })
      .then((res) => setQuotes(res.data.quotes))
      .catch(() => setQuotes([]));
  }, [tenantId, canReadQuotes]);

  async function save(fields) {
    setSaving(true);
    try {
      setSaveError(null);
      await api.patch(`/api/accounts/${tenantId}`, fields, { params: { source } });
      await load();
    } catch (err) {
      // El backend manda `code` cuando el perfil no puede editar un campo:
      // conviene mostrar ese motivo, ya traducido, y no un mensaje genérico.
      setSaveError(tApiError(err, 'accountDetail.saveError'));
    } finally {
      setSaving(false);
    }
  }

  // El objetivo se guarda al salir del campo, no en cada tecla: escribir «12»
  // pasa por «1», y ese 1 no debe quedar registrado como un cambio.
  function saveTrainingTarget() {
    const empty = trainingDraft === '' || trainingDraft === null;
    const value = empty ? null : Math.max(0, Math.trunc(Number(trainingDraft)));
    if (value !== null && Number.isNaN(value)) {
      setTrainingDraft(account.training_target ?? '');
      return;
    }
    if ((account.training_target ?? null) === value) return;
    save({ training_target: value });
  }

  if (loading) return <Layout><Skeleton variant="rectangular" height={480} /></Layout>;
  if (errorKey && !account) return <Layout><Alert severity="error">{t(errorKey)}</Alert></Layout>;
  if (!account) return null;

  return (
    <Layout>
      <Button variant="text" onClick={() => navigate(-1)} sx={{ mb: 1, color: '#531253' }}>
        {t('common.back')}
      </Button>
      {errorKey && <Alert severity="error" sx={{ mb: 2 }}>{t(errorKey)}</Alert>}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>{saveError}</Alert>
      )}

      <Paper sx={{ p: 2.4, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h5">{account.organization_name}</Typography>
          {account.is_internal && (
            <Box component="span" sx={{
              fontSize: 9.5, fontWeight: 600, letterSpacing: '0.11em',
              backgroundColor: '#531253', color: '#fff', px: 0.9, py: '2px', borderRadius: '3px',
            }}>{t('accounts.internalBadge')}</Box>
          )}
          <TierChip tier={account.plan} />
          <StatusChip band={account.band} />
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: bands[account.band].color }}>
            {account.score}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3.5, mt: 2 }}>
          <Fact label={t('accountDetail.created')}>{fmtDate(account.created_at)}</Fact>
          <Fact label={t('accountDetail.seatsActive')}>
            {t('common.ofTotal', { active: account.active_30d, total: account.total_users })}
            {account.contracted_seats > 0 && (
              <Box component="span" sx={{ color: '#847782', fontSize: 11.5, ml: 0.8 }}>
                {t('accountDetail.contracted', { contracted: account.contracted_seats })}
              </Box>
            )}
          </Fact>
          {account.free_users > 0 && (
            <Fact label={t('accountDetail.freeUsers')}>
              {account.free_users}
              <Box component="span" sx={{ color: '#847782', fontSize: 11.5, ml: 0.8 }}>
                {t('accountDetail.freeUsersHint', {
                  active: account.free_active_30d, total: account.free_users,
                })}
              </Box>
            </Fact>
          )}
          <Fact label={t('accountDetail.renewal')}>
            <TextField
              type="date" size="small" variant="standard" disabled={saving || !canEditCommercial}
              value={account.renewal_date ? account.renewal_date.slice(0, 10) : ''}
              onChange={(e) => save({ renewal_date: e.target.value })}
              sx={{ '& input': { fontSize: 14, fontFamily: 'Saira' } }}
            />
          </Fact>
          <Fact label={t('accountDetail.renewalStatus')}>
            <TextField
              select size="small" variant="standard" disabled={saving || !canEditCommercial}
              value={account.renewal_status}
              onChange={(e) => save({ renewal_status: e.target.value })}
              sx={{ minWidth: 130, '& .MuiSelect-select': { fontSize: 14, fontFamily: 'Saira' } }}
            >
              {Object.keys(renewalStatuses).map((key) => (
                <MenuItem key={key} value={key} sx={{ fontSize: 14 }}>{t(`renewalStatus.${key}`)}</MenuItem>
              ))}
            </TextField>
          </Fact>
          <Fact label={t('accountDetail.currentStatus')}><RenewalChip status={account.renewal_status} /></Fact>
          <Fact label={t('accountDetail.billingFrequency')}>
            <TextField
              select size="small" variant="standard" disabled={saving || !canEditCommercial}
              value={account.billing_frequency || ''}
              onChange={(e) => save({ billing_frequency: e.target.value || null })}
              sx={{ minWidth: 110, '& .MuiSelect-select': { fontSize: 14, fontFamily: 'Saira' } }}
            >
              <MenuItem value="" sx={{ fontSize: 14 }}>—</MenuItem>
              {BILLING_FREQUENCIES.map((key) => (
                <MenuItem key={key} value={key} sx={{ fontSize: 14 }}>{t(`billingFrequency.${key}`)}</MenuItem>
              ))}
            </TextField>
          </Fact>
          <Fact label={t('accountDetail.lastRenewal')}>
            <TextField
              type="date" size="small" variant="standard" disabled={saving || !canEditCommercial}
              value={account.last_renewal_date ? account.last_renewal_date.slice(0, 10) : ''}
              onChange={(e) => save({ last_renewal_date: e.target.value })}
              sx={{ '& input': { fontSize: 14, fontFamily: 'Saira' } }}
            />
          </Fact>
          <Fact label={t('accountDetail.sessions')}>
            {account.sessions_total}
            {account.last_session_at && (
              <Box component="span" sx={{ color: '#847782', fontSize: 11.5, ml: 0.8 }}>
                {t('accountDetail.lastSession', { date: fmtDate(account.last_session_at) })}
              </Box>
            )}
            {account.sessions_pending > 0 && (
              <Box component="span" sx={{ color: '#FF6666', fontSize: 11.5, ml: 0.8, fontWeight: 500 }}>
                {t('accountDetail.sessionsPending', { count: account.sessions_pending })}
              </Box>
            )}
          </Fact>
          <TrainingMeter
            t={t}
            account={account}
            draft={trainingDraft}
            onDraft={setTrainingDraft}
            onSave={saveTrainingTarget}
            disabled={saving || !canEditCs}
          />
        </Box>

        {/* Campos que viven sólo en el store interno. Nombre y responsables
            comparten rejilla para que etiquetas y controles queden alineados. */}
        <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #E4E0E2' }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 1.5fr) 1fr 1fr' },
            gap: 2.5, alignItems: 'start',
          }}>
            <Box>
              <FieldLabel>{t('accountDetail.orgName')}</FieldLabel>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small" fullWidth variant="standard"
                  disabled={saving || !canEditCs}
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  placeholder={account.product_org_name || t('accountDetail.orgNamePlaceholder')}
                  sx={{ '& input': { fontSize: 14, fontFamily: 'Saira' } }}
                />
                <Button
                  size="small" variant="contained"
                  disabled={saving || !canEditCs
                    || nameDraft === (account.display_name || account.product_org_name || '')}
                  onClick={() => save({ display_name: nameDraft || null })}
                  sx={{ fontSize: 12, flexShrink: 0 }}
                >{t('common.save')}</Button>
              </Box>
              <Typography sx={{ fontSize: 11.5, color: '#847782', mt: 0.6 }}>
                {account.product_org_name
                  ? t('accountDetail.inProduct', { name: account.product_org_name })
                  : t('accountDetail.noProductName')}
                {' '}{t('accountDetail.savedInternally')}
              </Typography>
            </Box>

            <OwnerSelect
              t={t}
              label={t('accountDetail.csOwner')}
              value={account.cs_owner_id}
              disabled={saving || !canEditCs}
              options={owners.filter((o) => ['cs_agent', 'admin'].includes(o.app_role))}
              onChange={(id) => save({ cs_owner_id: id })}
            />
            <OwnerSelect
              t={t}
              label={t('accountDetail.salesOwner')}
              value={account.sales_owner_id}
              disabled={saving || !canEditCommercial}
              options={owners.filter((o) => ['sales', 'admin'].includes(o.app_role))}
              onChange={(id) => save({ sales_owner_id: id })}
            />
          </Box>

          {/* Clasificación: dos interruptores hermanos, no anidados. */}
          <Box sx={{
            display: 'flex', gap: 5, flexWrap: 'wrap',
            mt: 2.5, pt: 2, borderTop: '1px solid #F0ECEE',
          }}>
            <ClassificationSwitch
              label={t('accountDetail.internalAccount')}
              hint={account.is_internal_override === null
                ? t('accountDetail.internalDetected', {
                    internal: account.internal_users, total: account.registered_users,
                  })
                : t('accountDetail.internalManual')}
              checked={Boolean(account.is_internal)}
              disabled={saving || !canEditCs}
              onChange={(v) => save({ is_internal: v })}
            />
            <ClassificationSwitch
              label={t('accountDetail.inactiveAccount')}
              hint={account.is_inactive_override === null
                ? (account.is_inactive_reason
                    ? t(`accountDetail.inactiveReason.${account.is_inactive_reason}`)
                    : t('accountDetail.inactiveNone'))
                : t('accountDetail.inactiveManual')}
              checked={Boolean(account.is_inactive)}
              disabled={saving || !canEditCs}
              onChange={(v) => save({ is_inactive: v })}
            />
            <ClassificationSwitch
              label={t('accountDetail.studentAccount')}
              hint={account.is_student_override === null
                ? t('accountDetail.studentDetected', {
                    edu: account.edu_users, total: account.registered_users,
                  })
                : t('accountDetail.studentManual')}
              checked={Boolean(account.is_student)}
              disabled={saving || !canEditCs}
              onChange={(v) => save({ is_student: v })}
            />
          </Box>
        </Box>
      </Paper>

      <Box sx={{
        display: 'grid', alignItems: 'start', gap: 2,
        gridTemplateColumns: { xs: '1fr', lg: '1.4fr 1fr' },
      }}>
        {/* Columna ancha: lo que necesita ancho para leerse — las barras del
            score, el texto de la bitácora y la tabla de usuarios, que trae
            cuatro columnas y en la angosta salía apretada. */}
        <Box sx={{ display: 'grid', gap: 2, alignContent: 'start', minWidth: 0 }}>
          <Paper sx={{ p: 2.2 }}>
            <Typography sx={{
              fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: '#847782', mb: 1.8,
            }}>{t('accountDetail.whyScore')}</Typography>

            {Object.entries(account.score_parts).map(([key, value]) => (
              <Box key={key} sx={{ mb: 1.6 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Tooltip
                    title={t(`accountDetail.tips.${key}`)}
                    placement="top-start"
                    arrow
                    slotProps={{ tooltip: { sx: { fontSize: 12, lineHeight: 1.5, maxWidth: 330, p: 1.4 } } }}
                  >
                    <Typography sx={{
                      fontSize: 12.5, cursor: 'help',
                      textDecoration: 'underline dotted', textUnderlineOffset: '3px',
                      textDecorationColor: '#C9BFC7',
                    }}>
                      {t(`accountDetail.parts.${key}`)}
                      <Box component="span" sx={{ color: '#847782', ml: 0.8, fontSize: 11 }}>
                        {t('accountDetail.weight', {
                          percent: Math.round(account.score_weights[key] * 100),
                        })}
                      </Box>
                    </Typography>
                  </Tooltip>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: colorFor(value) }}>
                    {value}
                  </Typography>
                </Box>
                <Box sx={{ height: 6, borderRadius: 3, backgroundColor: '#EDE6E3', overflow: 'hidden' }}>
                  <Box sx={{ width: `${value}%`, height: '100%', backgroundColor: colorFor(value) }} />
                </Box>
              </Box>
            ))}

            {account.flags.length > 0 && (
              <Box sx={{ mt: 2.4 }}>
                {account.flags.map((f) => (
                  <Typography key={f.code} sx={{ fontSize: 12.5, color: '#AE5B61' }}>▲ {tFlag(f)}</Typography>
                ))}
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 2.2 }}>
            <AccountTimeline tenantId={tenantId} source={source} onChanged={load} />
          </Paper>

          <Paper sx={{ p: 2.2 }}>
            <Typography sx={{
              fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: '#847782', mb: 1.2,
            }}>{t('accountDetail.usersCount', { count: users.length })}</Typography>
            <UsersTable rows={users} loading={false} />
          </Paper>
        </Box>

        {/* Columna angosta: listas de un renglón por cosa — con quién hablas,
            qué le debes, qué le cotizaste y cuándo lo viste. */}
        <Box sx={{ display: 'grid', gap: 2, alignContent: 'start', minWidth: 0 }}>
          {account.subscription_type === 'ENTERPRISE' && (
            <Paper sx={{ p: 2.2 }}>
              <AccountImplementation
                tenantId={tenantId} source={source} account={account} owners={owners}
              />
            </Paper>
          )}

          <Paper sx={{ p: 2.2 }}>
            <AccountContacts tenantId={tenantId} source={source} />
          </Paper>

          <Paper sx={{ p: 2.2 }}>
            <AccountTasks tenantId={tenantId} source={source} owners={owners} onChanged={load} />
          </Paper>

          {canReadQuotes && (
            <Paper sx={{ p: 2.2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
                <Typography sx={{
                  fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: '#847782',
                }}>{t('quote.accountQuotes')}</Typography>
                <Typography sx={{ fontSize: 13, color: '#FF6666', fontWeight: 600 }}>
                  {quotes.length}
                </Typography>
              </Box>

              {quotes.length === 0 && (
                <Typography sx={{ fontSize: 13.5, color: '#847782' }}>—</Typography>
              )}

              {quotes.slice(0, 5).map((q) => (
                <Box
                  key={q.quote_id}
                  onClick={() => navigate(`/quotes/${q.quote_id}`)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, py: 0.9, cursor: 'pointer',
                    borderBottom: '1px solid #F0ECEE', '&:last-of-type': { borderBottom: 'none' },
                    '&:hover': { backgroundColor: '#FFF0F0' },
                  }}
                >
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
                      #{q.q_folio}
                      <Box component="span" sx={{
                        fontSize: 9.5, fontWeight: 600, letterSpacing: '0.08em', ml: 1,
                        px: 0.7, py: '1px', borderRadius: '3px',
                        backgroundColor: q.is_renewal ? '#F3E9F3' : '#EDE6E3',
                        color: q.is_renewal ? '#531253' : '#6B5F68',
                      }}>
                        {q.is_renewal ? t('quote.isRenewal') : t('quote.newClient')}
                      </Box>
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: '#847782' }}>
                      {fmtDate(q.createdAt)}{q.owner_name ? ` · ${q.owner_name}` : ''}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {fmtCurrency.getCurrencyFormat(q.total || 0, q.currency || 'MXN')}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}

          <Paper sx={{ p: 2.2 }}>
            <AccountSessions tenantId={tenantId} source={source} onChanged={load} />
          </Paper>
        </Box>
      </Box>
    </Layout>
  );
}
